import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { DeliveryModal } from '../components/DeliveryModal';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, FilePdf, Fire, CheckCircle, Truck, Eye, X, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OrderDetailPage = () => {
  const { orderId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [extinguishers, setExtinguishers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfPreviewVariant, setPdfPreviewVariant] = useState('complete');
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      const [orderResponse, extinguishersResponse] = await Promise.all([
        axios.get(`${API}/orders/${orderId}`),
        axios.get(`${API}/extinguishers?order_id=${orderId}`)
      ]);

      setOrder(orderResponse.data);
      setExtinguishers(extinguishersResponse.data);
      setSelectedTechnician(orderResponse.data.assigned_technician_id || '');

      if (user?.role === 'admin' || user?.role === 'reception') {
        setLoadingTechnicians(true);
        try {
          const usersResponse = await axios.get(`${API}/users`);
          setTechnicians(usersResponse.data.filter(u => u.role === 'technician'));
        } catch (error) {
          logger.error('Error fetching technicians:', error);
          toast.error('Error al cargar técnicos');
        } finally {
          setLoadingTechnicians(false);
        }
      }
    } catch (error) {
      logger.error('Error fetching order details:', error);
      toast.error('Error al cargar los detalles');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTechnician = async () => {
    if (!selectedTechnician) {
      toast.error('Selecciona un técnico');
      return;
    }

    try {
      await axios.put(`${API}/orders/${orderId}/assign-technician?technician_id=${selectedTechnician}`);
      toast.success('Técnico asignado exitosamente');
      fetchOrderDetails();
    } catch (error) {
      logger.error('Error assigning technician:', error);
      toast.error('Error al asignar técnico');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/reports/order/${orderId}/pdf`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      if (response.data.size === 0) {
        toast.error('El PDF está vacío');
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orden_${order.order_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado exitosamente');
    } catch (error) {
      logger.error('Error downloading PDF:', error);
      if (error.response) {
        toast.error(`Error al descargar PDF: ${error.response.status}`);
      } else {
        toast.error('Error al descargar PDF');
      }
    }
  };

  const handleDownloadCondensedPDF = async () => {
    try {
      const response = await axios.get(`${API}/reports/order/${orderId}/pdf/condensed`, {
        responseType: 'blob',
        headers: { 'Accept': 'application/pdf' }
      });
      if (response.data.size === 0) {
        toast.error('El PDF condensado está vacío');
        return;
      }
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orden_${order.order_number}_condensado.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF condensado descargado exitosamente');
    } catch (error) {
      logger.error('Error downloading condensed PDF:', error);
      toast.error('Error al descargar PDF condensado');
    }
  };

  const handlePreviewPDF = async (variant = 'complete') => {
    setLoadingPdf(true);
    try {
      const endpoint = variant === 'condensed'
        ? `${API}/reports/order/${orderId}/pdf/condensed`
        : `${API}/reports/order/${orderId}/pdf`;
      const response = await axios.get(endpoint, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      if (response.data.size === 0) {
        toast.error('El PDF está vacío');
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
      setPdfPreviewVariant(variant);
      setShowPdfPreview(true);
    } catch (error) {
      logger.error('Error loading PDF preview:', error);
      toast.error('Error al cargar vista previa');
    } finally {
      setLoadingPdf(false);
    }
  };

  const closePdfPreview = () => {
    setShowPdfPreview(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleDeleteOrder = async () => {
    const confirmMsg =
      `¿Estás seguro de eliminar la orden ${order.order_number}?\n\n` +
      `Esta acción NO se puede deshacer. Se eliminarán ${extinguishers.length} extintor(es) y todas sus etapas y fotografías.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success('Orden eliminada exitosamente');
      navigate('/orders');
    } catch (error) {
      logger.error('Error deleting order:', error);
      toast.error(error?.response?.data?.detail || 'Error al eliminar la orden');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-96">
          <p className="text-lg">Cargando...</p>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="text-center">
          <p className="text-lg">Orden no encontrada</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/orders')}
              data-testid="back-button"
              className="hover:bg-secondary"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">
                {order.order_number}
              </h1>
              <p className="text-base text-muted-foreground mt-2">{order.client_name}</p>
            </div>
          </div>
          <div className="flex gap-3">
            {(user?.role === 'admin' || user?.role === 'reception') && order.status === 'completed' && (
              <Button
                onClick={() => setShowDeliveryModal(true)}
                data-testid="mark-delivered-button"
                className="bg-foreground hover:bg-foreground/90 text-background"
              >
                <Truck size={20} className="mr-2" weight="fill" />
                Marcar como Entregada
              </Button>
            )}
            <Button
              onClick={() => handlePreviewPDF('complete')}
              disabled={loadingPdf}
              data-testid="preview-pdf-button"
              variant="outline"
              className="border-primary text-primary hover:bg-primary/10"
            >
              <Eye size={20} className="mr-2" weight="fill" />
              {loadingPdf ? 'Cargando...' : 'Vista Previa'}
            </Button>
            <Button
              onClick={() => handlePreviewPDF('condensed')}
              disabled={loadingPdf}
              data-testid="preview-pdf-condensed-button"
              variant="outline"
              className="border-primary text-primary hover:bg-primary/10"
            >
              <Eye size={20} className="mr-2" weight="fill" />
              {loadingPdf ? 'Cargando...' : 'Previa Condensado'}
            </Button>
            <Button
              onClick={handleDownloadPDF}
              data-testid="download-pdf-button"
              className="bg-primary hover:bg-primary/90"
            >
              <FilePdf size={20} className="mr-2" weight="fill" />
              Descargar PDF
            </Button>
            <Button
              onClick={handleDownloadCondensedPDF}
              data-testid="download-pdf-condensed-button"
              variant="outline"
              className="border-primary text-primary hover:bg-primary/10"
            >
              <FilePdf size={20} className="mr-2" weight="fill" />
              PDF Condensado
            </Button>
            {user?.role === 'admin' && (
              <Button
                onClick={handleDeleteOrder}
                data-testid="delete-order-button"
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/10"
              >
                <Trash size={20} className="mr-2" weight="fill" />
                Eliminar Orden
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="p-8 border border-border bg-background">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-6">
                Información de la Orden
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Cliente
                  </p>
                  <p className="mt-1 font-semibold">{order.client_name}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Lugar de Recepción
                  </p>
                  <p className="mt-1 font-semibold">{order.reception_location}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Recibido de
                  </p>
                  <p className="mt-1 font-semibold">{order.reception_received_by_name}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Fecha de Recepción
                  </p>
                  <p className="mt-1 font-mono text-sm">
                    {new Date(order.reception_date).toLocaleDateString('es-MX')}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Estado
                  </p>
                  <span
                    className={`inline-block mt-1 px-3 py-1 text-xs font-bold uppercase tracking-wider border ${
                      order.status === 'pending'
                        ? 'border-muted bg-muted text-foreground'
                        : order.status === 'in_progress'
                        ? 'border-primary bg-primary/10 text-primary'
                        : order.status === 'completed'
                        ? 'border-foreground bg-foreground/10 text-foreground'
                        : 'border-foreground bg-foreground text-background'
                    }`}
                  >
                    {order.status === 'pending' && 'Pendiente'}
                    {order.status === 'in_progress' && 'En Proceso'}
                    {order.status === 'completed' && 'Completada'}
                    {order.status === 'delivered' && 'Entregada'}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Creado por
                  </p>
                  <p className="mt-1 font-semibold">{order.created_by_name}</p>
                </div>
              </div>
            </div>

            <div className="p-8 border border-border bg-background">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-6">
                Extintores ({extinguishers.length})
              </h2>
              <div className="space-y-4">
                {extinguishers.map((ext) => (
                  <div
                    key={ext.id}
                    className="p-6 border border-border bg-secondary hover:bg-secondary/70"
                    data-testid={`extinguisher-${ext.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <Fire size={32} weight="fill" className="text-primary" />
                        <div>
                          <p className="font-bold text-lg">Extintor #{extinguishers.indexOf(ext) + 1}</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {ext.agent_type} - {ext.capacity_value} {ext.capacity_unit}
                          </p>
                          <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                            {ext.manufacture_date && (
                              <span>Fabricación: {new Date(ext.manufacture_date).toLocaleDateString('es-MX')}</span>
                            )}
                            {ext.has_hydrostatic_test && ext.has_hydrostatic_test !== 'N/A' && (
                              <span>Prueba Hidrostática: {ext.has_hydrostatic_test}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-3">
                            <span className="text-xs font-bold uppercase tracking-wider">
                              Etapa {Math.min(ext.current_stage, 3)} de 3
                            </span>
                            {ext.stage_1_completed && <CheckCircle size={16} weight="fill" className="text-foreground" />}
                            {ext.stage_2_completed && <CheckCircle size={16} weight="fill" className="text-foreground" />}
                            {ext.stage_3_completed && <CheckCircle size={16} weight="fill" className="text-foreground" />}
                          </div>
                          
                          {/* Botones para ver/editar etapas completadas */}
                          <div className="flex flex-wrap gap-3 mt-4">
                            {ext.stage_1_completed && (
                              <div className="space-y-1">
                                <div className="flex gap-1">
                                  <Link to={`/extinguisher/${ext.id}/stage/1?view=true`}>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      data-testid={`view-stage-1-${ext.id}`}
                                      className="text-xs hover:bg-secondary"
                                    >
                                      Ver E1: Recepción
                                    </Button>
                                  </Link>
                                  {user?.role === 'admin' && (
                                    <Link to={`/extinguisher/${ext.id}/stage/1?edit=true`}>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        data-testid={`edit-stage-1-${ext.id}`}
                                        className="text-xs hover:bg-amber-100 text-amber-700 border-amber-300"
                                      >
                                        Editar
                                      </Button>
                                    </Link>
                                  )}
                                </div>
                                {ext.stage_1_completed_at && (
                                  <p className="text-[10px] text-muted-foreground font-mono">
                                    {new Date(ext.stage_1_completed_at).toLocaleString('es-MX', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}<br/>{ext.stage_1_completed_by_name}
                                  </p>
                                )}
                              </div>
                            )}
                            {ext.stage_2_completed && (
                              <div className="space-y-1">
                                <div className="flex gap-1">
                                  <Link to={`/extinguisher/${ext.id}/stage/2?view=true`}>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      data-testid={`view-stage-2-${ext.id}`}
                                      className="text-xs hover:bg-secondary"
                                    >
                                      Ver E2: Taller
                                    </Button>
                                  </Link>
                                  {user?.role === 'admin' && (
                                    <Link to={`/extinguisher/${ext.id}/stage/2?edit=true`}>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        data-testid={`edit-stage-2-${ext.id}`}
                                        className="text-xs hover:bg-amber-100 text-amber-700 border-amber-300"
                                      >
                                        Editar
                                      </Button>
                                    </Link>
                                  )}
                                </div>
                                {ext.stage_2_completed_at && (
                                  <p className="text-[10px] text-muted-foreground font-mono">
                                    {new Date(ext.stage_2_completed_at).toLocaleString('es-MX', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}<br/>{ext.stage_2_completed_by_name}
                                  </p>
                                )}
                              </div>
                            )}
                            {ext.stage_3_completed && (
                              <div className="space-y-1">
                                <div className="flex gap-1">
                                  <Link to={`/extinguisher/${ext.id}/stage/3?view=true`}>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      data-testid={`view-stage-3-${ext.id}`}
                                      className="text-xs hover:bg-secondary"
                                    >
                                      Ver E3: Refacciones
                                    </Button>
                                  </Link>
                                  {user?.role === 'admin' && (
                                    <Link to={`/extinguisher/${ext.id}/stage/3?edit=true`}>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        data-testid={`edit-stage-3-${ext.id}`}
                                        className="text-xs hover:bg-amber-100 text-amber-700 border-amber-300"
                                      >
                                        Editar
                                      </Button>
                                    </Link>
                                  )}
                                </div>
                                {ext.stage_3_completed_at && (
                                  <p className="text-[10px] text-muted-foreground font-mono">
                                    {new Date(ext.stage_3_completed_at).toLocaleString('es-MX', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}<br/>{ext.stage_3_completed_by_name}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {(user?.role === 'technician' || user?.role === 'admin') && ext.current_stage <= 3 && !ext.stage_3_completed && (
                        <Link to={`/extinguisher/${ext.id}/stage/${ext.current_stage}`}>
                          <Button
                            size="sm"
                            data-testid={`edit-stage-${ext.id}`}
                            className="bg-primary hover:bg-primary/90"
                          >
                            Completar Etapa {ext.current_stage}
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {(user?.role === 'admin' || user?.role === 'reception') && (
              <div className="p-8 border border-border bg-background">
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-6">
                  Asignar Técnico
                </h2>
                {order.assigned_technician_name ? (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">
                      Técnico Asignado
                    </p>
                    <p className="font-semibold">{order.assigned_technician_name}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {loadingTechnicians ? (
                      <p className="text-sm text-muted-foreground">Cargando técnicos...</p>
                    ) : (
                      <>
                        <select
                          value={selectedTechnician}
                          onChange={(e) => setSelectedTechnician(e.target.value)}
                          data-testid="technician-select"
                          className="w-full px-4 py-2 border border-border bg-background text-foreground"
                        >
                          <option value="">Seleccionar técnico...</option>
                          {technicians.map((tech) => (
                            <option key={tech.id} value={tech.id}>
                              {tech.name}
                            </option>
                          ))}
                        </select>
                        <Button
                          onClick={handleAssignTechnician}
                          disabled={!selectedTechnician}
                          data-testid="assign-technician-button"
                          className="w-full bg-primary hover:bg-primary/90"
                        >
                          Asignar
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {(order.reception_signature || order.reception_received_by_name) && (
              <div className="p-8 border border-border bg-background" data-testid="reception-signature-section">
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-6 flex items-center gap-2">
                  <CheckCircle size={24} weight="fill" />
                  Firma de Recepción
                </h2>
                <div className="space-y-4">
                  {order.reception_received_by_name && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                        Entregado por (Cliente)
                      </p>
                      <p className="mt-1 font-semibold">{order.reception_received_by_name}</p>
                    </div>
                  )}
                  {order.reception_signature && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">
                        Firma
                      </p>
                      <div className="border border-border p-2 bg-white inline-block">
                        <img
                          src={order.reception_signature}
                          alt="Firma de recepción del cliente"
                          className="max-h-32"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {order.delivery_info && (
              <div className="p-8 border border-border bg-background">
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-6 flex items-center gap-2">
                  <Truck size={24} weight="fill" />
                  Información de Entrega
                </h2>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                      Recibido por
                    </p>
                    <p className="mt-1 font-semibold">{order.delivery_info.received_by_name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                      Entregado por
                    </p>
                    <p className="mt-1 font-semibold">{order.delivery_info.delivered_by_name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                      Fecha de Entrega
                    </p>
                    <p className="mt-1 font-mono text-sm">
                      {new Date(order.delivery_info.delivered_at).toLocaleString('es-MX', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  {order.delivery_info.observations && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                        Observaciones
                      </p>
                      <p className="mt-1 text-sm">{order.delivery_info.observations}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {showDeliveryModal && (
        <DeliveryModal
          order={order}
          onClose={() => setShowDeliveryModal(false)}
          onDelivered={() => {
            fetchOrderDetails();
          }}
        />
      )}

      {/* Modal Vista Previa PDF */}
      {showPdfPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" data-testid="pdf-preview-modal">
          <div className="relative w-full h-full max-w-6xl max-h-[95vh] m-4 bg-white rounded-lg overflow-hidden flex flex-col">
            {/* Header del modal */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <div className="flex items-center gap-3">
                <FilePdf size={24} className="text-primary" weight="fill" />
                <h3 className="text-lg font-bold">
                  Vista Previa {pdfPreviewVariant === 'condensed' ? 'Condensado' : 'Completo'} - {order?.order_number}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={pdfPreviewVariant === 'condensed' ? handleDownloadCondensedPDF : handleDownloadPDF}
                  size="sm"
                  className="bg-primary hover:bg-primary/90"
                  data-testid="pdf-preview-download-button"
                >
                  <FilePdf size={18} className="mr-2" weight="fill" />
                  Descargar
                </Button>
                <Button
                  onClick={closePdfPreview}
                  variant="outline"
                  size="sm"
                  data-testid="close-pdf-preview"
                >
                  <X size={18} className="mr-1" />
                  Cerrar
                </Button>
              </div>
            </div>
            
            {/* Visor PDF */}
            <div className="flex-1 overflow-hidden bg-gray-200">
              {pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  title="Vista Previa PDF"
                  className="w-full h-full"
                  style={{ minHeight: '500px' }}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">Cargando PDF...</p>
                </div>
              )}
            </div>
            
            {/* Tip */}
            <div className="p-2 text-center text-xs text-gray-500 bg-gray-50 border-t">
              Tip: La primera página muestra la matriz resumen en horizontal
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
